/** Idle timeout before silently switching to text-only (2 minutes). */
export const AVATAR_IDLE_TIMEOUT_MS = 120_000;

/**
 * Grace when Anam closes the session before the client timer fires.
 * Anam enforces limits server-side and often disconnects slightly early.
 */
export const ANAM_TIMEOUT_GRACE_MS = 60_000;

export type DisconnectClassifierInput = {
  sessionStartedAt: number | null;
  lastActivityAt: number | null;
  pendingMaxSessionStop: boolean;
  avatarMaxSessionLengthSeconds: number;
  idleTimerPending?: boolean;
  maxSessionTimerPending?: boolean;
  now?: number;
};

export type AnamTimeoutKind = 'max_session' | 'idle' | null;

function elapsedMs(input: DisconnectClassifierInput): number {
  if (input.sessionStartedAt == null) return 0;
  const now = input.now ?? Date.now();
  return now - input.sessionStartedAt;
}

function timeoutGraceMs(limitMs: number): number {
  return Math.min(ANAM_TIMEOUT_GRACE_MS, Math.max(5_000, Math.floor(limitMs * 0.2)));
}

export function isMaxSessionTimeout(input: DisconnectClassifierInput): boolean {
  if (input.pendingMaxSessionStop) return true;
  if (input.sessionStartedAt == null) return false;
  const maxMs = input.avatarMaxSessionLengthSeconds * 1000;
  return elapsedMs(input) >= maxMs - timeoutGraceMs(maxMs);
}

function idleElapsedMs(input: DisconnectClassifierInput): number {
  if (input.lastActivityAt == null) return 0;
  const now = input.now ?? Date.now();
  return now - input.lastActivityAt;
}

export function isIdleTimeout(input: DisconnectClassifierInput): boolean {
  if (input.lastActivityAt == null) return false;
  return idleElapsedMs(input) >= AVATAR_IDLE_TIMEOUT_MS - timeoutGraceMs(AVATAR_IDLE_TIMEOUT_MS);
}

/** Prefer max_session when both limits could apply. */
export function classifyAnamTimeout(input: DisconnectClassifierInput): AnamTimeoutKind {
  if (isMaxSessionTimeout(input)) return 'max_session';
  if (isIdleTimeout(input)) return 'idle';
  return null;
}
